import React, { useState } from "react";
import { HiOutlineMail, HiOutlineLocationMarker } from "react-icons/hi";
import { FaLinkedin, FaGithub } from "react-icons/fa";
import Swal from "sweetalert2";
import {
  trackContactFormSubmit,
  trackSocialClick,
  trackCVDownload,
} from "../utils/analytics";

const Contact = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    mensaje: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Usar API_URL de .env en producción, localhost en desarrollo
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:3001/api";
      const response = await fetch(`${apiUrl}/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        trackContactFormSubmit(true);
        await Swal.fire({
          icon: "success",
          title: "¡Mensaje enviado!",
          text: "Gracias por contactarme. Te responderé pronto.",
          confirmButtonColor: "#2563eb",
        });
        setFormData({ nombre: "", email: "", mensaje: "" });
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      trackContactFormSubmit(false);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al enviar el mensaje. Por favor intenta de nuevo.",
        confirmButtonColor: "#2563eb",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      name="contact"
      className="w-full min-h-screen bg-slate-50 dark:bg-slate-900 py-20 lg:py-24 transition-colors duration-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Contacto
          </h2>
          <div className="w-24 h-1 bg-blue-600 dark:bg-blue-500 mx-auto mb-6"></div>
          <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            ¿Tienes un proyecto en mente? ¡Hablemos!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Información de contacto */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200 dark:border-slate-700">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                Información de contacto
              </h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 dark:bg-blue-500 p-3 rounded-full flex-shrink-0">
                    <HiOutlineMail className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      Email
                    </p>
                    <a
                      href="mailto:abrahanm7@gmail.com"
                      onClick={() => trackSocialClick("Email")}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      abrahanm7@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 dark:bg-blue-500 p-3 rounded-full flex-shrink-0">
                    <svg
                      className="text-white text-xl"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      width="20"
                      height="20"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      Teléfono
                    </p>
                    <a
                      href="tel:+543834381427"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                    >
                      +54 383 438-1427
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 dark:bg-blue-500 p-3 rounded-full flex-shrink-0">
                    <HiOutlineLocationMarker className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      Ubicación
                    </p>
                    <p className="text-slate-600 dark:text-slate-300">
                      Catamarca, Argentina
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-600">
                <p className="font-semibold text-slate-900 dark:text-white mb-4">
                  Encuéntrame en:
                </p>
                <div className="flex space-x-4">
                  <a
                    href="https://www.linkedin.com/in/abraham-molina/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick("LinkedIn")}
                    className="bg-blue-600 dark:bg-blue-500 text-white p-3 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-300 hover:scale-110 shadow-md"
                    aria-label="LinkedIn"
                  >
                    <FaLinkedin className="text-xl" />
                  </a>
                  <a
                    href="https://github.com/Abrahanm7"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick("GitHub")}
                    className="bg-slate-800 dark:bg-slate-700 text-white p-3 rounded-full hover:bg-slate-900 dark:hover:bg-slate-600 transition-all duration-300 hover:scale-110 shadow-md"
                    aria-label="GitHub"
                  >
                    <FaGithub className="text-xl" />
                  </a>
                </div>
              </div>

              {/* Botón de descarga de CV */}
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-600">
                <a
                  href="/Cv-Abraham-Molina.pdf"
                  download
                  onClick={trackCVDownload}
                  className="flex items-center justify-center space-x-3 w-full bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-500 dark:to-blue-600 text-white font-semibold py-3.5 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 dark:hover:from-blue-600 dark:hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Descargar CV</span>
                </a>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200 dark:border-slate-700">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                ¿Por qué trabajar conmigo?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-slate-700 dark:text-slate-300">
                    <strong>Experiencia real</strong> con proyectos para
                    empresas
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-slate-700 dark:text-slate-300">
                    Integraciones complejas (AFIP, Stripe, APIs)
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-slate-700 dark:text-slate-300">
                    Stack PERN completo
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-slate-700 dark:text-slate-300">
                    Metodologías ágiles
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-slate-700 dark:text-slate-300">
                    Comunicación constante
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Formulario de contacto */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200 dark:border-slate-700">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
              Envíame un mensaje
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full p-4 border-2 border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none transition-all duration-300"
                  type="text"
                  name="nombre"
                  placeholder="Tu nombre completo"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full p-4 border-2 border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none transition-all duration-300"
                  type="email"
                  name="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Mensaje <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="Cuéntame sobre tu proyecto..."
                  className="w-full p-4 border-2 border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent outline-none transition-all duration-300 resize-none"
                  name="mensaje"
                  rows="6"
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-blue-600 dark:bg-blue-500 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 group ${
                  isSubmitting
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700 dark:hover:bg-blue-600 shadow-md hover:shadow-lg"
                }`}
              >
                <span>{isSubmitting ? "Enviando..." : "Enviar mensaje"}</span>
                {!isSubmitting && (
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
